/**
 * Bridge to the local Android `CallNative` Capacitor plugin (audio routing,
 * ongoing-call foreground service, lock-screen call actions). Every helper is
 * a silent no-op on web / iOS / older APKs without the plugin — we reach into
 * `window.Capacitor.Plugins.*` at call time so the web bundle never imports
 * a native-only module.
 */

const PENDING_KEY = 'allure_pending_call_action';
const PENDING_TTL_MS = 90000;

const getCapacitor = () => {
  try {
    const cap = window?.Capacitor;
    return cap?.isNativePlatform?.() ? cap : null;
  } catch {
    return null;
  }
};

const getPlugin = (name) => getCapacitor()?.Plugins?.[name] || null;

export const isNativeAndroid = () => {
  try { return getCapacitor()?.getPlatform?.() === 'android'; } catch { return false; }
};

export const isCallNativeAvailable = () => Boolean(getPlugin('CallNative'));

const invoke = async (method, args = {}) => {
  const plugin = getPlugin('CallNative');
  if (!plugin || typeof plugin[method] !== 'function') return null;
  try {
    return await plugin[method](args);
  } catch (err) {
    console.warn(`[CallNative] ${method} failed`, err);
    return null;
  }
};

export const nativeStartCallSession = (speakerOn = false) => invoke('startCallSession', { speakerOn: Boolean(speakerOn) });
export const nativeSetSpeaker = (on) => invoke('setSpeaker', { on: Boolean(on) });
export const nativeEndCallSession = () => invoke('endCallSession');
export const nativeShowOngoingCall = ({ callId, name, conversationId, role, state, startedAt, endUrl }) => invoke('showOngoingCall', {
  callId: callId || '',
  name: name || 'Allure contact',
  conversationId: conversationId || '',
  role: role || 'guest',
  state: state || 'active',
  startedAt: Math.round(startedAt || Date.now()),
  endUrl: endUrl || '',
});
export const nativeClearOngoingCall = () => invoke('clearOngoingCall');
export const nativeClearIncoming = (callId) => (callId ? invoke('clearIncomingCall', { callId }) : Promise.resolve(null));
export const nativeGetPendingCallAction = async () => {
  const result = await invoke('getPendingCallAction');
  return result?.action ? result : null;
};

const subscribe = (plugin, event, handler) => {
  if (!plugin || typeof plugin.addListener !== 'function') return () => {};
  let handle;
  try {
    handle = plugin.addListener(event, handler);
  } catch {
    return () => {};
  }
  return () => {
    Promise.resolve(handle).then((h) => h?.remove?.()).catch(() => {});
  };
};

export const addNativeCallActionListener = (handler) => subscribe(getPlugin('CallNative'), 'callAction', handler);
export const addNativeBackButtonListener = (handler) => subscribe(getPlugin('App'), 'backButton', handler);
export const addNativeAppStateListener = (handler) => subscribe(getPlugin('App'), 'appStateChange', (state) => handler(Boolean(state?.isActive)));
export const nativeMinimizeApp = () => {
  try { getPlugin('App')?.minimizeApp?.(); } catch { /* noop */ }
};

// A call action that must survive a hard navigation (role switch reload).
export const readPendingCallAction = () => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.action || Date.now() - (parsed.at || 0) > PENDING_TTL_MS) {
      window.sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const writePendingCallAction = (action) => {
  try { window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...action, at: Date.now() })); } catch { /* noop */ }
};

export const clearPendingCallAction = () => {
  try { window.sessionStorage.removeItem(PENDING_KEY); } catch { /* noop */ }
};
